import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import BusScreen from '@/screens/BusScreen.vue'
import LookupRoutesScreen from '@/screens/LookupRoutesScreen.vue'
import LookupStopsScreen from '@/screens/LookupStopsScreen.vue'
import PlanScreen from '@/screens/PlanScreen.vue'
import RouteScreen from '@/screens/RouteScreen.vue'
import StopBusesScreen from '@/screens/StopBusesScreen.vue'
import StopRoutesScreen from '@/screens/StopRoutesScreen.vue'
import TrackScreen from '@/screens/TrackScreen.vue'
import { positionOfState, syncNavigation } from '@/utils/scrollMemory'

/**
 * Tabs are path segments, not `?tab=` query params — every tab in the mockups is
 * its own URL. The three bare paths (`/`, `/stops/:stopId`, `/lookup`) exist only
 * to redirect to their first tab, so a shared or hand-typed link still resolves.
 *
 * Two different routes end in `buses` (`/stops/:stopId/buses` and
 * `/routes/:routeCode/buses/:vehicleId`). They cannot collide — the first segment
 * differs — but ALWAYS navigate by name. A hand-built path is one typo away from
 * meaning something else entirely.
 *
 * Names and params are compile-checked: `typed-routes.d.ts` declares the 8 named
 * routes, so a typo is a build error rather than a silent fall-through to the
 * catch-all. **Adding a route here means adding it there** — they are two
 * hand-written lists, and `__tests__/typed-routes.test.ts` fails if they disagree.
 */
const routes: RouteRecordRaw[] = [
  { path: '/', redirect: { name: 'track' } },
  { path: '/track', name: 'track', component: TrackScreen },
  { path: '/plan', name: 'plan', component: PlanScreen },

  {
    path: '/stops/:stopId',
    /**
     * `to.params` is typed as a union across every route, so it cannot be forwarded
     * wholesale to one named target. The narrow is sound rather than convenient: this
     * record only matches `/stops/:stopId`, so `stopId` is always present here.
     */
    redirect: (to) => ({
      name: 'stop-buses',
      params: { stopId: (to.params as { stopId: string }).stopId },
    }),
  },
  { path: '/stops/:stopId/buses', name: 'stop-buses', component: StopBusesScreen, props: true },
  { path: '/stops/:stopId/routes', name: 'stop-routes', component: StopRoutesScreen, props: true },

  { path: '/routes/:routeCode', name: 'route', component: RouteScreen, props: true },
  {
    path: '/routes/:routeCode/buses/:vehicleId',
    name: 'bus',
    component: BusScreen,
    props: true,
  },

  { path: '/lookup', redirect: { name: 'lookup-routes' } },
  { path: '/lookup/routes', name: 'lookup-routes', component: LookupRoutesScreen },
  { path: '/lookup/stops', name: 'lookup-stops', component: LookupStopsScreen },

  { path: '/:pathMatch(.*)*', redirect: { name: 'track' } },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  /**
   * Intentionally a no-op, and intentionally still here.
   *
   * `savedPosition` is always `{ top: 0 }` in this app: vue-router computes it
   * from `window.scrollY`, and the window never scrolls — `AppFrame` is
   * `h-dvh overflow-hidden` and every screen scrolls inside a descendant. The
   * real restoration lives in `useScrollRestore`.
   *
   * Do not delete this hook to "remove dead code". Merely DEFINING a
   * `scrollBehavior` is what makes vue-router set
   * `history.scrollRestoration = 'manual'`; without it the browser starts
   * restoring window scroll on its own and fights the containers.
   */
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

/**
 * Only a `popstate` can tell a forward navigation from a push: arriving at entry
 * 7 by pressing forward and by pushing a new screen both report position 7.
 * `syncNavigation` needs the difference to know whether the entries beyond this
 * one are still reachable or have just been discarded.
 */
let poppedSinceLastNavigation = false
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    poppedSinceLastNavigation = true
  })
}

// Registered here, at router-creation time, so it runs before any component's
// own `afterEach` and before RouterView mounts the screen that will read the store.
router.afterEach(() => {
  const isPop = poppedSinceLastNavigation
  poppedSinceLastNavigation = false
  syncNavigation(positionOfState(router.options.history.state), isPop)
})

export default router
